import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Building2, Plus, BarChart3, CheckCircle, XCircle, Star,
  IndianRupee, MapPin, Shield, Share2, Copy, Send
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../ui/dialog";
import { toast } from "sonner";

const formatINR = (v) => {
  if (!v) return "N/A";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

const ProjectComparator = ({
  apiBase,
  leadId,
  prefill,
  showShareButton = true,
  isPublicView = false,
  linkToken,
}) => {
  const [projects, setProjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState(prefill?.project_ids || []);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [creatingLink, setCreatingLink] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const endpoint = isPublicView
          ? `${apiBase}/tools/projects-public`
          : `${apiBase}/projects`;
        const res = await axios.get(endpoint);
        setProjects(res.data);

        // Auto-compare if prefilled project_ids
        if (prefill?.project_ids?.length >= 2) {
          setSelectedIds(prefill.project_ids);
        }
      } catch { }
      finally { setLoading(false); }
    };
    if (apiBase) init();

    if (isPublicView && linkToken && apiBase) {
      axios.post(`${apiBase}/tools/track-open`, { token: linkToken }).catch(() => {});
    }
  }, [apiBase, isPublicView, linkToken, prefill]);

  const toggleSelect = (pid) => {
    setSelectedIds((prev) =>
      prev.includes(pid) ? prev.filter((id) => id !== pid) : prev.length < 4 ? [...prev, pid] : prev
    );
  };

  const handleCompare = async () => {
    if (selectedIds.length < 2) { toast.error("Select at least 2 projects"); return; }
    setComparing(true);
    try {
      const endpoint = isPublicView
        ? `${apiBase}/tools/compare-projects-public`
        : `${apiBase}/decision-support/compare`;
      const res = await axios.post(endpoint, { project_ids: selectedIds });
      setComparison(res.data);

      // Log interaction
      if ((leadId || linkToken) && apiBase) {
        const names = res.data.projects?.map((p) => p.name).join(", ");
        axios.post(`${apiBase}/tools/log-calculation`, {
          lead_id: leadId,
          link_token: linkToken,
          tool_type: "project_comparator",
          inputs: { project_ids: selectedIds },
          outputs: { project_names: names, project_count: selectedIds.length },
        }).catch(() => {});
      }
    } catch { toast.error("Comparison failed"); }
    finally { setComparing(false); }
  };

  const createShareLink = async () => {
    if (!leadId) { toast.error("Cannot share without a lead"); return; }
    setCreatingLink(true);
    try {
      const res = await axios.post(`${apiBase}/tools/create-link`, {
        lead_id: leadId,
        tool_type: "project_comparator",
        prefill_data: { project_ids: selectedIds },
        expires_in_days: 30,
      });
      setShareLink(res.data);
      setShareDialogOpen(true);
    } catch { toast.error("Failed to create link"); }
    finally { setCreatingLink(false); }
  };

  const copyLink = () => {
    if (shareLink?.full_url) {
      navigator.clipboard.writeText(shareLink.full_url);
      toast.success("Link copied!");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  // Comparison results view
  if (comparison) {
    const projs = comparison.projects || [];
    const best = comparison.best_values || {};
    return (
      <div className="space-y-4" data-testid="project-comparator-results">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Comparison Results</h3>
          <Button variant="outline" size="sm" onClick={() => setComparison(null)}>Back</Button>
        </div>

        {/* Side-by-side cards */}
        <div className={`grid gap-3 ${projs.length === 2 ? "grid-cols-2" : projs.length === 3 ? "grid-cols-3" : "grid-cols-2 lg:grid-cols-4"}`}>
          {projs.map((p) => (
            <Card key={p.project_id || p.name} className={best.lowest_price === p.name ? "border-green-300" : ""}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  {best.lowest_price === p.name && <Star className="w-4 h-4 text-green-500 fill-green-500" />}
                  <h4 className="text-sm font-bold text-slate-900 truncate">{p.name}</h4>
                </div>
                {p.developer && <p className="text-xs text-slate-500">{p.developer}</p>}
                <div className="flex items-center gap-1 text-xs text-slate-600"><MapPin className="w-3 h-3" />{p.location}</div>
                <div className="space-y-1.5 mt-2">
                  <div className="flex justify-between text-xs"><span className="text-slate-500">Price Range</span><span className="font-medium">{formatINR(p.price_min)} - {formatINR(p.price_max)}</span></div>
                  {p.price_per_sqft && <div className="flex justify-between text-xs"><span className="text-slate-500">Price/sqft</span><span className="font-medium">{formatINR(p.price_per_sqft)}</span></div>}
                  <div className="flex justify-between text-xs"><span className="text-slate-500">Configs</span><span className="font-medium">{(p.configurations || []).join(", ") || "N/A"}</span></div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">RERA</span>
                    <span>{p.rera_compliant ? <CheckCircle className="w-3.5 h-3.5 text-green-500 inline" /> : <XCircle className="w-3.5 h-3.5 text-red-400 inline" />}</span>
                  </div>
                  {p.possession_date && <div className="flex justify-between text-xs"><span className="text-slate-500">Possession</span><span className="font-medium">{p.possession_date}</span></div>}
                </div>
                {(p.amenities || []).length > 0 && (
                  <div className="mt-2">
                    <p className="text-[10px] text-slate-400 mb-1">Amenities ({p.amenities.length})</p>
                    <div className="flex flex-wrap gap-1">{p.amenities.slice(0, 5).map((a, i) => <Badge key={i} variant="outline" className="text-[10px]">{a}</Badge>)}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Best values summary */}
        <Card className="bg-green-50/50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-green-800 mb-2">Quick Highlights</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              {best.lowest_price && <div><span className="text-slate-500">Best Price: </span><span className="font-bold text-green-700">{best.lowest_price}</span></div>}
              {best.lowest_price_per_sqft && <div><span className="text-slate-500">Best /sqft: </span><span className="font-bold text-green-700">{best.lowest_price_per_sqft}</span></div>}
              {best.most_amenities && <div><span className="text-slate-500">Most Amenities: </span><span className="font-bold text-green-700">{best.most_amenities}</span></div>}
              {best.earliest_possession && <div><span className="text-slate-500">Earliest Possession: </span><span className="font-bold text-green-700">{best.earliest_possession}</span></div>}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Selection view
  return (
    <div className="space-y-4" data-testid="project-comparator">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" /> Project Comparator
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Select 2-4 projects to compare side-by-side</p>
        </div>
        <div className="flex gap-2">
          {showShareButton && leadId && !isPublicView && selectedIds.length >= 2 && (
            <Button variant="outline" size="sm" onClick={createShareLink} disabled={creatingLink} data-testid="share-comparator-btn">
              <Share2 className="w-4 h-4 mr-1" />{creatingLink ? "Creating..." : "Share"}
            </Button>
          )}
          <Button size="sm" onClick={handleCompare} disabled={selectedIds.length < 2 || comparing} data-testid="compare-projects-btn">
            <BarChart3 className="w-4 h-4 mr-1" /> Compare ({selectedIds.length})
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {projects.length === 0 && <p className="text-sm text-slate-400 col-span-3 text-center py-6">No projects available</p>}
        {projects.map((p) => {
          const sel = selectedIds.includes(p.project_id);
          return (
            <button key={p.project_id} onClick={() => toggleSelect(p.project_id)}
              className={`p-3 rounded-lg border-2 text-left transition-all ${sel ? "border-blue-400 bg-blue-50 ring-1 ring-blue-200" : "border-slate-200 hover:border-slate-300"}`}
              data-testid={`project-${p.project_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm font-medium ${sel ? "text-blue-700" : "text-slate-800"}`}>{p.name}</p>
                  {p.developer && <p className="text-[10px] text-slate-500">{p.developer}</p>}
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{p.location}</p>
                </div>
                {sel && <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />}
              </div>
              <div className="mt-2 text-xs text-slate-600">
                {p.price_min ? `${formatINR(p.price_min)} - ${formatINR(p.price_max)}` : "Price TBD"}
              </div>
            </button>
          );
        })}
      </div>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Share Comparison with Lead</DialogTitle></DialogHeader>
          {shareLink && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">The lead will see these {selectedIds.length} projects pre-selected for comparison.</p>
              <div className="flex items-center gap-2">
                <Input value={shareLink.full_url} readOnly className="text-sm" data-testid="comparator-share-link" />
                <Button variant="outline" size="icon" onClick={copyLink}><Copy className="w-4 h-4" /></Button>
              </div>
              <p className="text-xs text-slate-500">Link expires in 30 days</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>Close</Button>
            <Button onClick={copyLink}><Copy className="w-4 h-4 mr-2" /> Copy Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectComparator;
